import React from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    LinearProgress,
    Stack,
    Typography,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import {
    PLANNER_QUOTE_STATES,
    jobQuoteLocked,
    quoteRevLabel,
    quoteStateColor,
    quoteStateLabel,
    quoteStateOf,
} from "./plannerQuoteState";

function formatWhen(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function JobLifecycleBar({
    job,
    busy = false,
    onSetState,
    onRevise,
    compact = false,
}) {
    if (!job) return null;
    const state = quoteStateOf(job.quoteState);
    const locked = jobQuoteLocked(job);
    const workDone = job.workDone ?? 0;
    const workTotal = job.workTotal ?? 0;
    const workPct = workTotal ? Math.round((workDone / workTotal) * 100) : 0;
    const scopeDone = job.scopeDone ?? 0;
    const scopeTotal = job.scopeTotal ?? 0;
    const checkDone = job.rollupDone ?? job.checkDone ?? 0;
    const checkTotal = job.rollupTotal ?? job.checkTotal ?? 0;
    const generated = formatWhen(job.quoteGeneratedAt);
    const latestNote = (job.quoteRevisions || [])[0]?.note;

    return (
        <Stack spacing={1.25} sx={{ mt: compact ? 0 : 1.5 }}>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                <Chip
                    size="small"
                    color={quoteStateColor(state)}
                    icon={locked ? <LockIcon /> : <LockOpenIcon />}
                    label={quoteStateLabel(state)}
                />
                <Chip size="small" variant="outlined" label={quoteRevLabel(job.quoteRev)} />
                {job.quoteDirty ? (
                    <Chip size="small" color="warning" label="Quote out of date" />
                ) : null}
                {generated ? (
                    <Typography variant="caption" color="text.secondary">
                        Quoted {generated}
                    </Typography>
                ) : null}
            </Stack>

            <Box>
                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Job progress
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {workPct}% · {workDone}/{workTotal}
                    </Typography>
                </Stack>
                <LinearProgress variant="determinate" value={workPct} sx={{ height: 8, borderRadius: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                    {scopeTotal
                        ? `${scopeDone}/${scopeTotal} items complete`
                        : "No child items yet"}
                    {checkTotal ? ` · ${checkDone}/${checkTotal} checklist` : ""}
                </Typography>
            </Box>

            {locked ? (
                <Alert severity="success" variant="outlined">
                    Locked to {quoteRevLabel(job.quoteRev)}. Work status and checklists can still move. Revise to change
                    scope or pricing.
                </Alert>
            ) : null}
            {job.quoteDirty ? (
                <Alert severity="warning" variant="outlined">
                    Scope changed after {quoteRevLabel(job.quoteRev)} was generated. Build quote again so the live quote
                    matches the planner.
                </Alert>
            ) : null}
            {latestNote && !compact ? (
                <Typography variant="caption" color="text.secondary">
                    Last: {latestNote}
                </Typography>
            ) : null}

            {!compact ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {state === "draft" ? (
                        <Button size="small" variant="outlined" disabled={busy} onClick={() => onSetState("finalized")}>
                            Mark finalized
                        </Button>
                    ) : null}
                    {state === "finalized" || state === "revised" ? (
                        <Button size="small" disabled={busy} onClick={() => onSetState("draft")}>
                            Back to rough draft
                        </Button>
                    ) : null}
                    {state === "finalized" ? (
                        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                            {PLANNER_QUOTE_STATES.find((row) => row.id === "finalized")?.blurb}
                        </Typography>
                    ) : null}
                    {state === "revised" ? (
                        <Button size="small" variant="outlined" disabled={busy} onClick={() => onSetState("finalized")}>
                            Mark finalized
                        </Button>
                    ) : null}
                    {locked ? (
                        <Button size="small" color="warning" variant="contained" disabled={busy} onClick={onRevise}>
                            Revise & unlock
                        </Button>
                    ) : null}
                </Stack>
            ) : null}
        </Stack>
    );
}
